import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import Support from './Support';
import Navbar from '../pages/Register/Navbar';
import Footer from '../pages/Register/Footer';

const ExpiredLink = ({ isMobile }) => {
  const navigate = useNavigate();

  const pathVariants = {
    hidden: { pathLength: 0, opacity: 0 },
    visible: {
      pathLength: 1,
      opacity: 1,
      transition: {
        pathLength: {
          type: 'spring',
          duration: 1.8,
          bounce: 0.3,
        },
        opacity: {
          duration: 0.5,
        },
      },
    },
  };

  const generateRipples = () => {
    return Array.from({ length: 3 }).map((_, i) => (
      <motion.div
        key={i}
        className='absolute inset-0 rounded-full border border-red-600'
        initial={{ scale: 0.8, opacity: 0.8 }}
        animate={{
          scale: [1, 1.5, 2],
          opacity: [0.5, 0.3, 0],
        }}
        transition={{
          duration: 2,
          delay: 1.8 + i * 0.5,
          repeat: Infinity,
          repeatDelay: i * 1,
        }}
      />
    ));
  };

  const generateParticles = () => {
    return Array.from({ length: 30 }).map((_, i) => {
      const size = Math.random() * 12 + 4;
      const angle = Math.random() * Math.PI * 2;
      const distance = Math.random() * 150 + 50;
      const duration = Math.random() * 2.5 + 1.5;
      const delay = Math.random() * 0.8;
      const colors = ['#ef4444', '#dc2626', '#b91c1c'];

      return (
        <motion.div
          key={i}
          className='absolute rounded-full'
          style={{
            width: size,
            height: size,
            backgroundColor: colors[Math.floor(Math.random() * colors.length)],
            opacity: 0,
          }}
          initial={{
            x: 0,
            y: 0,
            opacity: 0,
            scale: 0.5,
          }}
          animate={{
            x: Math.cos(angle) * distance,
            y: Math.sin(angle) * distance,
            opacity: [0, 0.9, 0],
            scale: [0.5, 1.2, 0.8],
            rotate: 360,
          }}
          transition={{
            duration: duration,
            delay: delay,
            repeat: Infinity,
            repeatDelay: 0.3,
            ease: 'easeOut',
          }}
        />
      );
    });
  };

  const pageStyles = {
    backgroundImage: "url('/bg.jpg')",
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'repeat',
    backgroundAttachment: 'fixed',
    position: 'relative',
  };

  return (
    <>
      <div className='flex flex-col min-h-screen' style={pageStyles}>
        <div className='sticky top-0 left-0 right-0 z-[3]'>
          <div className='bg-transparent'>
            <Navbar isRegisterPage={true} />
          </div>
        </div>

        <Support />

        <main className='flex-grow relative z-[1]'>
          <div className='relative min-h-[calc(100vh-105px)] flex flex-col items-center justify-center px-4 sm:px-6 lg:px-8'>
            <motion.div
              className='absolute inset-0 bg-gradient-to-b from-red-900/5 to-red-600/10 z-[1]'
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 2 }}
            />

            <div className='relative z-[2] w-full max-w-lg mx-auto mt-8 sm:mt-20'>
              <div className='w-full max-w-md mx-auto text-center'>
                <div className='relative mb-8'>
                  <div className='relative w-36 h-36 mx-auto flex items-center justify-center'>
                    {generateRipples()}

                    <div className='absolute inset-0 flex items-center justify-center'>
                      {generateParticles()}
                    </div>

                    <motion.div
                      className='relative w-36 h-36 bg-white rounded-full shadow-xl flex items-center justify-center z-10'
                      initial={{ scale: 0, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={{
                        type: 'spring',
                        stiffness: 260,
                        damping: 20,
                        delay: 0.5,
                      }}>
                      <svg
                        width='150'
                        height='150'
                        viewBox='0 0 150 150'
                        fill='none'
                        xmlns='http://www.w3.org/2000/svg'
                        className='w-full h-full p-6'>
                        <motion.circle
                          cx='75'
                          cy='75'
                          r='60'
                          stroke='#dc2626'
                          strokeWidth='6'
                          strokeLinecap='round'
                          fill='transparent'
                          initial={{ pathLength: 0 }}
                          animate={{ pathLength: 1 }}
                          transition={{
                            duration: 1.5,
                            ease: 'easeInOut',
                            delay: 0.8,
                          }}
                        />

                        <motion.path
                          d='M50 50L100 100'
                          stroke='#dc2626'
                          strokeWidth='8'
                          strokeLinecap='round'
                          strokeLinejoin='round'
                          fill='transparent'
                          variants={pathVariants}
                          initial='hidden'
                          animate='visible'
                          transition={{ delay: 1.5 }}
                        />

                        <motion.path
                          d='M100 50L50 100'
                          stroke='#dc2626'
                          strokeWidth='8'
                          strokeLinecap='round'
                          strokeLinejoin='round'
                          fill='transparent'
                          variants={pathVariants}
                          initial='hidden'
                          animate='visible'
                          transition={{ delay: 1.7 }}
                        />
                      </svg>
                    </motion.div>
                  </div>
                </div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 2 }}>
                  <h1 className='text-3xl font-bold text-red-600 mb-4'>
                    Link Expired
                  </h1>

                  <p className='text-lg text-gray-600 mb-8'>
                    This subscription payment link has expired or is no longer
                    valid.
                  </p>

                  {isMobile ? (
                    <motion.button
                      whileHover={{
                        scale: 1.05,
                        boxShadow: '0 10px 25px -5px rgba(220, 38, 38, 0.4)',
                      }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => navigate('/store')}
                      className='bg-red-600 hover:bg-red-700 text-white font-bold py-4 px-10 rounded-lg shadow-lg transition-all duration-300 w-full max-w-xs mx-auto mb-4'>
                      Back to App
                    </motion.button>
                  ) : (
                    <motion.button
                      whileHover={{
                        scale: 1.05,
                        boxShadow: '0 10px 25px -5px rgba(220, 38, 38, 0.4)',
                      }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => navigate('/')}
                      className='bg-red-600 hover:bg-red-700 text-white font-bold py-4 px-10 rounded-lg shadow-lg transition-all duration-300 w-full max-w-xs mx-auto mb-4'>
                      Back Home
                    </motion.button>
                  )}

                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className='flex items-center justify-center gap-2 text-red-600 hover:text-red-700 font-semibold py-3 px-6 mx-auto mt-2'
                    onClick={() =>
                      window.open('https://wa.me/96176624565', '_blank')
                    }>
                    Contact Support
                  </motion.button>
                </motion.div>
              </div>
            </div>
          </div>
        </main>

        <Footer ThanksPage={false} />
      </div>
    </>
  );
};

export default ExpiredLink;
